import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { tipo_documento: "contratto", tipo_comunicazione: "assunzione", tipologia_contratto: "", mansione: "", qualifica: "", livello: "",
  ccnl: "", tipo_orario: "", ore_settimanali: "", data_inizio: "", data_fine: "", retribuzione: "", luogo_lavoro: "", note: "" };

const Field = ({ id, label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label htmlFor={id}>{label}</Label>{children}</div>;

export const ContractFormDialog = ({ open, onOpenChange, contract, initial, employeeId, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(contract ? { ...EMPTY, ...contract, ore_settimanali: contract.ore_settimanali ?? "" } : { ...EMPTY, ...(initial || {}) }); }, [open, contract, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setV = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, stato, created_at, origine, email_subject, email_from, email_date, filename, file_hash, has_file, employee_created, ...rest } = form;
    const body = { ...rest, employee_id: employeeId, ore_settimanali: rest.ore_settimanali === "" ? null : Number(String(rest.ore_settimanali).replace(",", ".")) };
    try {
      if (contract) await api.put(`/contracts/${contract.id}`, body);
      else await api.post("/contracts", body);
      toast.success(contract ? "Contratto aggiornato" : "Contratto inserito");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="contract-form-dialog">
        <DialogHeader><DialogTitle>{contract ? "Modifica contratto" : initial?.tipo_comunicazione === "proroga" ? "Registra proroga" : "Nuovo contratto"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field id="tipo_com" label="Tipo comunicazione">
            <Select value={form.tipo_comunicazione || "assunzione"} onValueChange={(v) => setV("tipo_comunicazione", v)}>
              <SelectTrigger id="tipo_com" data-testid="contract-tipo-com-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assunzione">Assunzione</SelectItem><SelectItem value="proroga">Proroga</SelectItem>
                <SelectItem value="trasformazione">Trasformazione</SelectItem><SelectItem value="cessazione">Cessazione</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field id="tipologia" label="Tipologia contratto"><Input id="tipologia" placeholder="es. Tempo determinato part-time" value={form.tipologia_contratto} onChange={set("tipologia_contratto")} data-testid="contract-tipologia-input" /></Field>
          <Field id="mansione" label="Mansione"><Input id="mansione" value={form.mansione} onChange={set("mansione")} data-testid="contract-mansione-input" /></Field>
          <Field id="ore" label="Ore settimanali"><Input id="ore" inputMode="decimal" value={form.ore_settimanali} onChange={set("ore_settimanali")} data-testid="contract-ore-input" /></Field>
          <Field id="qualifica" label="Qualifica"><Input id="qualifica" value={form.qualifica} onChange={set("qualifica")} data-testid="contract-qualifica-input" /></Field>
          <Field id="livello" label="Livello"><Input id="livello" value={form.livello} onChange={set("livello")} data-testid="contract-livello-input" /></Field>
          <Field id="inizio" label="Data inizio"><DatePicker id="inizio" value={form.data_inizio} onChange={(v) => setV("data_inizio", v)} testId="contract-inizio-input" /></Field>
          <Field id="fine" label="Data fine (vuota = indeterminato)"><DatePicker id="fine" value={form.data_fine} onChange={(v) => setV("data_fine", v)} placeholder="Indeterminato" testId="contract-fine-input" /></Field>
          <Field id="ccnl" label="CCNL"><Input id="ccnl" value={form.ccnl} onChange={set("ccnl")} data-testid="contract-ccnl-input" /></Field>
          <Field id="orario" label="Tipo orario"><Input id="orario" placeholder="es. Part-time orizzontale" value={form.tipo_orario} onChange={set("tipo_orario")} data-testid="contract-orario-input" /></Field>
          <Field id="note" label="Note" className="sm:col-span-2"><Textarea id="note" rows={2} value={form.note} onChange={set("note")} data-testid="contract-note-input" /></Field>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="contract-form-cancel">Annulla</Button>
            <Button type="submit" disabled={saving} data-testid="contract-form-submit">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
